<script setup lang="ts">
import {onUpdated, reactive, ref} from "vue";
import InstallPrompt from "./InstallPrompt.vue";
import AdminSideBar from "./AdminSideBar.vue";
import {Button, ConfirmDialog, ConfirmPopup} from "@/Components/Library";
import {useRouter} from "vue-router";
import online from "@/Composables/online";
import Notifications from "./Notifications.vue";
import {breakpointsTailwind, useBreakpoints} from "@vueuse/core";
import useAuth from "@/Composables/auth";
import {useMainStore} from "@/Stores/store";
import MenuDrawer from "./MenuDrawer.vue";
import {useInstall} from "@/Composables/useInstall";

const $store = useMainStore();
const isOnline = online();
const router = useRouter();
const {logOut} = useAuth();
const breakpoints = useBreakpoints(breakpointsTailwind);
const {isInstallAvailable, showInstallPrompt} = useInstall();

const state = reactive({
    show: {
        drawer: false,
    },
});

const userMenu = ref([
    {
        label: "Feed",
        icon: "prime:list",
        to: "feed",
    },
    {
        label: "Contacts",
        icon: "prime:address-book",
        to: "contacts.index",
    },
    {
        label: "Groups",
        icon: "prime:users",
        to: "groups.index",
    },
    {
        label: "Stimuli",
        icon: "prime:image",
        to: "stimuli.index",
    },
    {
        label: "Profile",
        icon: "prime:user",
        to: "profile.edit",
    },
    {
        label: "Log out",
        icon: "prime:sign-out",
        command: () => {
            logOut();
        },
    },
]);

const toggleSidebar = (show: boolean | null = null, breakpoint: string | null = null) => {
    if (!breakpoint || breakpoints.smallerOrEqual("lg").value) {
        $store.show.expandedSidebar = show ?? !$store.show.expandedSidebar;
    }
};

onUpdated(() => {
    // console.log('App.vue updated');
});
</script>
<template>
    <div class="relative flex h-dvh grow">
        <AdminSideBar @navigate="toggleSidebar(false, 'lgAndDown')" />
        <div
            class="w-full overflow-y-auto transition-all"
            :class="[$store.show.expandedSidebar ? 'lg:ml-80' : 'lg:ml-16']"
            @click="toggleSidebar(false, 'lgAndDown')"
        >
            <main class="flex h-dvh grow flex-col overflow-y-auto">
                <header v-if="$store.user.id" class="sticky top-0 z-20 bg-askr-green px-3 py-1 text-white lg:px-6">
                    <div class="flex items-center py-2 md:px-0">
                        <Button
                            :icon="{icon: 'iconamoon:menu-burger-horizontal-bold', width: '26'}"
                            rounded
                            outlined
                            class="mr-2 h-6 w-6 transition lg:hidden"
                            @click.stop="$store.show.expandedSidebar = !$store.show.expandedSidebar"
                        />
                        <RouterLink :to="{name: 'admin.dashboard'}">
                            <h1 class="text-3xl font-black tracking-[-0.175rem] text-stone-100">ASKr</h1>
                        </RouterLink>
                        <div class="ml-auto flex items-center">
                            <Button
                                v-if="isInstallAvailable"
                                @click="showInstallPrompt"
                                icon="ic:round-install-mobile"
                                text
                                rounded
                                class="mr-3 text-white hover:bg-white/10"
                                title="Install app"
                            />
                            <Notifications />
                            <Button
                                class="ml-3"
                                text
                                rounded
                                aria-label="User"
                                @click="state.show.drawer = true"
                                :icon="{icon: 'iconamoon:menu-burger-horizontal-bold', width: '26'}"
                            />
                        </div>
                    </div>
                </header>
                <div class="w-full grow px-0 py-4 lg:px-3" @click="toggleSidebar(false, 'lgAndDown')">
                    <slot />
                </div>
            </main>
        </div>
        <ConfirmPopup class="max-w-md" dusk="confirm-popup">
            <template #message="{message}">
                <div class="p-5">
                    <strong v-if="message.header">{{ message.header }}</strong>
                    <br v-if="message.header" />
                    {{ message.message }}
                </div>
            </template>
        </ConfirmPopup>
        <ConfirmDialog class="max-w-sm" group="dialog" />
        <InstallPrompt />
        <MenuDrawer v-model="state.show.drawer" />
    </div>
</template>
<style scoped></style>
